import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import socketIOClient from "socket.io-client";
import { AuthContext } from "../context/authContext";
import { Helmet } from 'react-helmet';
import './VoiceChatRoom.css';
import SingingStage from '../components/karaoke/SingingStage';
import '../components/karaoke/karaoke.css';

const ENDPOINT = process.env.REACT_APP_API_END_POINT || "http://localhost:3005";
const CDN = 'https://files.geetsuhane.com/';

// Waitress items - drinks and snacks
const WAITRESS_ITEMS = [
    { id: 'coffee', emoji: '☕', name: 'Coffee' },
    { id: 'tea', emoji: '🍵', name: 'Chai' },
    { id: 'juice', emoji: '🧃', name: 'Juice' },
    { id: 'soda', emoji: '🥤', name: 'Soda' },
    { id: 'water', emoji: '💧', name: 'Water' },
    { id: 'samosa', emoji: '🥟', name: 'Samosa' },
    { id: 'cookies', emoji: '🍪', name: 'Cookies' },
    { id: 'cake', emoji: '🍰', name: 'Cake' },
    { id: 'popcorn', emoji: '🍿', name: 'Popcorn' },
    { id: 'chips', emoji: '🍟', name: 'Chips' }
];

export default function VoiceChatRoom() {
    const { currentUser } = useContext(AuthContext);
    const navigate = useNavigate();

    // Menu state
    const [menuOpen, setMenuOpen] = useState(false);

    // 9 seats in circle - seat 0 is host seat at top
    const [seats, setSeats] = useState(Array(9).fill(null));
    const [occupiedSeatIndex, setOccupiedSeatIndex] = useState(null);
    const [isHost, setIsHost] = useState(false);
    const recentSeatSwitch = useRef(false); // Flag to prevent server overriding local state after switch
    const [messages, setMessages] = useState([]);
    const [currentMessage, setCurrentMessage] = useState("");
    const [listeners, setListeners] = useState([]);
    const [roomInfo, setRoomInfo] = useState({ name: 'GS GupShup Room', listeners: 0 });
    const [isMuted, setIsMuted] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [debugInfo, setDebugInfo] = useState({ connections: 0, audioElements: 0 });
    const [showDebug, setShowDebug] = useState(false);
    const [speakingUsers, setSpeakingUsers] = useState({}); // Track who is speaking { oderId: true/false }
    const [micPermissionDenied, setMicPermissionDenied] = useState(false); // Track mic permission state
    const [requestingMic, setRequestingMic] = useState(false); // Prevent multiple mic requests

    // Waitress state
    const [waitressVisible, setWaitressVisible] = useState(false);
    const [waitressItems, setWaitressItems] = useState([]);
    const [myDrink, setMyDrink] = useState(null);
    const [takenItems, setTakenItems] = useState([]); // Items taken by user this session

    // Mobile audio unlock state
    const [audioUnlocked, setAudioUnlocked] = useState(false);
    const audioQueueRef = useRef([]); // Queue for bot audio while locked
    const sharedAudioRef = useRef(null); // Shared audio element for bot audio
    const audioContextRef = useRef(null); // Web Audio API context
    const isIOS = useRef(/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

    const socket = useRef(null);
    const peerConnections = useRef({});
    const localStream = useRef(null);
    const remoteAudioRefs = useRef({});
    const messagesEndRef = useRef(null);
    const pendingIceCandidates = useRef({}); // Queue ICE candidates until remote description is set

    // WebRTC configuration — TURN credentials come from the API now
    // (GET /v2/rtc/ice reads them from the box's .env) instead of living in
    // this bundle. STUN-only until the fetch lands; the ref keeps the latest.
    const rtcConfiguration = useRef({
        iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            { urls: 'stun:global.stun.twilio.com:3478' }
        ],
        iceCandidatePoolSize: 10,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require'
    });
    useEffect(() => {
        fetch('https://gsapi.geetsuhane.com/v2/rtc/ice')
            .then(r => r.json())
            .then(cfg => { if (cfg?.iceServers) rtcConfiguration.current = cfg; })
            .catch(() => {});
    }, []);

    // Create peer connection helper - defined early so it can be used in useEffect
    const createPeerConnectionRef = useRef(null);
    const initiateCallRef = useRef(null);

    // Allow anonymous users to listen (but not speak)
    const isAnonymous = !currentUser;
    const guestUserRef = useRef(null);
    if (isAnonymous && !guestUserRef.current) {
        guestUserRef.current = {
            SID: `guest_${Date.now()}`,
            name: 'Guest',
            picture: null,
            isGuest: true
        };
    }
    const activeUser = currentUser || guestUserRef.current;

    // Unlock audio - must be called from user interaction
    const unlockAudio = useCallback(() => {
        if (audioUnlocked) return;

        console.log('🔊 Unlocking audio... (iOS:', isIOS.current, ')');

        // Create shared audio element IMMEDIATELY on first interaction
        if (!sharedAudioRef.current) {
            const audio = document.createElement('audio');
            audio.id = 'shared-bot-audio';
            audio.playsInline = true;
            audio.autoplay = false;
            audio.preload = 'auto';
            audio.muted = false;
            audio.volume = 1.0;
            // iOS-specific attributes
            audio.setAttribute('playsinline', 'true');
            audio.setAttribute('webkit-playsinline', 'true');
            audio.setAttribute('x-webkit-airplay', 'allow');
            // For iOS 15+ audio session
            if ('webkitPreservesPitch' in audio) {
                audio.webkitPreservesPitch = true;
            }
            document.body.appendChild(audio);
            sharedAudioRef.current = audio;
            console.log('🔊 Created shared audio element');
        }

        // Create and unlock AudioContext SYNCHRONOUSLY (important for iOS)
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (!audioContextRef.current) {
                audioContextRef.current = new AudioContext({
                    sampleRate: 44100,
                    latencyHint: 'interactive'
                });
                console.log('🔊 Created AudioContext, state:', audioContextRef.current.state);
            }

            // Resume MUST happen synchronously in the gesture handler for iOS
            if (audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume();
                console.log('🔊 AudioContext resume called');
            }

            // Play silent buffer through AudioContext
            const silentBuffer = audioContextRef.current.createBuffer(1, 1, 22050);
            const source = audioContextRef.current.createBufferSource();
            source.buffer = silentBuffer;
            source.connect(audioContextRef.current.destination);
            source.start(0);
            console.log('🔊 Silent buffer played through AudioContext');
        } catch (e) {
            console.log('AudioContext unlock failed:', e.message);
        }

        // iOS Safari needs the audio element to play something from the gesture
        // Use a short silent MP3 data URL (more compatible than WAV on iOS)
        const silentMP3 = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7v/////////////////////////////////' +
            '//////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRwmHAAAAAAD/+1DEAAAGAAGn9AAAIgAANP8AAABMA0Gwgx3/8QHf/EBz8QHP4gO/gg/+CD7wgP/gh//gg//+Xu9hNpOITCExhMQHOc7u7jnP/lxznO7u7u93u7u7u4xu7u7u7vd3d3d3cYxjd3d3d3e7u7u7uMbu7u7u73d3d3d3GMY3d3d3d3u7u7u7jG7u7u7u93d3d3dxjGN3d3d3d7u7u7u4xu7u7u7vd3d3d3cYxv/7cMQPA8AAAaQAAAAgAAA0gAAABDGMbsAAANIAAAAQY3d3d3d3u7u7u7jG7u7u7u93d3d3dxjGN3d3d3d7u7u7u4xu7u7u7vd3d3d3cYxjd3d3d3e7u7u7uMbu7u7u73d3d3d3GMY3d3d3d3u7u7u7jG7u7u7u93d3d3dxjGN3d3d3d7u7u7u4xu7u7u7vd3d3d3cYxjd3d3d3e7u7u7uMbu7u7u73d3d3d3GMY3d3d3d3u7u7u4=';

        // Play silent audio through shared element - MUST be synchronous
        if (sharedAudioRef.current) {
            sharedAudioRef.current.src = silentMP3;
            const playPromise = sharedAudioRef.current.play();
            if (playPromise) {
                playPromise.then(() => {
                    console.log('🔊 Shared audio element unlocked successfully');
                }).catch((e) => {
                    console.log('Shared audio unlock failed:', e.message);
                    // For iOS, try again with load() first
                    if (isIOS.current) {
                        sharedAudioRef.current.load();
                        setTimeout(() => {
                            sharedAudioRef.current.play().catch(() => {});
                        }, 50);
                    }
                });
            }
        }

        // Also create a backup audio element for iOS
        if (isIOS.current) {
            try {
                const backupAudio = new Audio(silentMP3);
                backupAudio.volume = 1.0;
                backupAudio.play().then(() => {
                    console.log('🔊 Backup iOS audio played');
                }).catch(() => {});
            } catch (e) {}
        }

        setAudioUnlocked(true);
        console.log('🔊 Audio unlocked! Processing queued audio...');

        // Show volume reminder message
        setMessages(prev => [...prev, {
            user: 'System',
            text: '🔊 Speaker enabled! Turn up your device volume to hear the poems.',
            isSystem: true
        }]);

        // Process queued bot audio after a small delay to ensure audio is ready
        setTimeout(() => {
            if (audioQueueRef.current.length > 0 && playBotAudioRef.current) {
                console.log('🔊 Processing', audioQueueRef.current.length, 'queued audio items');
                const nextAudio = audioQueueRef.current.shift();
                playBotAudioRef.current(nextAudio);
            }
        }, 150);
    }, [audioUnlocked]);

    // Add document-level event listeners for audio unlock (catches all clicks/keys)
    useEffect(() => {
        const handleUserInteraction = () => {
            if (!audioUnlocked) {
                console.log('🔊 User interaction detected, unlocking audio...');
                unlockAudio();
            }
        };

        // Listen for any user interaction
        document.addEventListener('click', handleUserInteraction);
        document.addEventListener('touchstart', handleUserInteraction);
        document.addEventListener('keydown', handleUserInteraction);

        return () => {
            document.removeEventListener('click', handleUserInteraction);
            document.removeEventListener('touchstart', handleUserInteraction);
            document.removeEventListener('keydown', handleUserInteraction);
        };
    }, [audioUnlocked, unlockAudio]);

    // Play bot audio function ref (to avoid stale closures)
    const playBotAudioRef = useRef(null);

    // Play bot audio (only call after audio is unlocked)
    const playBotAudio = useCallback((audioData) => {
        const { audioContent, contentType, name } = audioData;

        console.log('🎵 playBotAudio called for:', name);
        console.log('🎵 Content type:', contentType);
        console.log('🎵 Audio content length:', audioContent?.length);
        console.log('🎵 sharedAudioRef.current:', !!sharedAudioRef.current);
        console.log('🎵 iOS device:', isIOS.current);

        // Must have shared audio element (created by unlockAudio)
        if (!sharedAudioRef.current) {
            console.log('🎵 No shared audio element - queueing audio');
            audioQueueRef.current.push(audioData);
            return;
        }

        try {
            const byteCharacters = atob(audioContent);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            // Use audio/mpeg for iOS compatibility
            const mimeType = isIOS.current ? 'audio/mpeg' : (contentType || 'audio/mpeg');
            const blob = new Blob([byteArray], { type: mimeType });
            const audioUrl = URL.createObjectURL(blob);

            console.log('🎵 Created blob URL:', audioUrl);
            console.log('🎵 Blob size:', blob.size, 'bytes');
            console.log('🎵 MIME type:', mimeType);

            // Always use shared audio element (unlocked by user interaction)
            const audio = sharedAudioRef.current;

            // For iOS, we need to load before setting src
            if (isIOS.current) {
                audio.pause();
                audio.currentTime = 0;
            }

            audio.src = audioUrl;
            audio.volume = 1.0;
            audio.muted = false;

            console.log('🎵 Audio element state before play:');
            console.log('   - paused:', audio.paused);
            console.log('   - muted:', audio.muted);
            console.log('   - volume:', audio.volume);
            console.log('   - readyState:', audio.readyState);

            // For iOS, wait for canplaythrough event
            const playAudio = () => {
                const playPromise = audio.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            console.log('🎵 ✅ Bot audio PLAYING:', name);
                            console.log('   - duration:', audio.duration);
                            console.log('   - paused:', audio.paused);
                        })
                        .catch(e => {
                            console.log('🎵 ❌ Bot audio play FAILED:', e.message);
                            console.log('   - Error name:', e.name);
                            // If play fails, user hasn't interacted yet
                            setMessages(prev => {
                                if (!prev.some(m => m.text === '🔊 Tap the red button above to enable audio!')) {
                                    return [...prev, { user: 'System', text: '🔊 Tap the red button above to enable audio!', isSystem: true }];
                                }
                                return prev;
                            });
                            // Queue this audio for retry when user interacts
                            audioQueueRef.current.unshift(audioData);
                        });
                }
            };

            // iOS needs the audio to be loaded first
            if (isIOS.current) {
                audio.load();
                // Wait for audio to be ready
                const onCanPlay = () => {
                    audio.removeEventListener('canplaythrough', onCanPlay);
                    playAudio();
                };
                audio.addEventListener('canplaythrough', onCanPlay);
                // Fallback timeout in case canplaythrough doesn't fire
                setTimeout(() => {
                    audio.removeEventListener('canplaythrough', onCanPlay);
                    if (audio.readyState >= 3) {
                        playAudio();
                    }
                }, 500);
            } else {
                playAudio();
            }

            audio.onended = () => {
                console.log('🎵 Audio ended, revoking URL');
                URL.revokeObjectURL(audioUrl);
                // Play next queued audio if any
                if (audioQueueRef.current.length > 0) {
                    console.log('🎵 Playing next queued audio...');
                    const nextAudio = audioQueueRef.current.shift();
                    if (playBotAudioRef.current) {
                        // Small delay between audios on iOS
                        setTimeout(() => {
                            playBotAudioRef.current(nextAudio);
                        }, isIOS.current ? 200 : 50);
                    }
                }
            };

            audio.onerror = (e) => {
                console.log('🎵 ❌ Audio error:', e);
                console.log('   - error code:', audio.error?.code);
                console.log('   - error message:', audio.error?.message);
                URL.revokeObjectURL(audioUrl);
            };
        } catch (e) {
            console.error('🎵 ❌ Error in playBotAudio:', e);
        }
    }, []);

    // Keep ref updated
    playBotAudioRef.current = playBotAudio;

    useEffect(() => {

        // Define WebRTC helper functions inside useEffect to avoid stale closures
        const createPeerConnection = (targetSocketId) => {
            if (peerConnections.current[targetSocketId]) {
                return peerConnections.current[targetSocketId];
            }

            console.log('Creating new peer connection for:', targetSocketId);
            const peerConnection = new RTCPeerConnection(rtcConfiguration.current);
            peerConnections.current[targetSocketId] = peerConnection;

            peerConnection.onicecandidate = event => {
                if (event.candidate) {
                    console.log('Sending ICE candidate to:', targetSocketId);
                    socket.current.emit('ice-candidate', { to: targetSocketId, candidate: event.candidate });
                }
            };

            peerConnection.ontrack = event => {
                console.log('=== RECEIVED REMOTE TRACK ===');
                console.log('From socket:', targetSocketId);
                console.log('Track kind:', event.track?.kind);
                console.log('Track readyState:', event.track?.readyState);
                console.log('Streams count:', event.streams?.length);

                if (event.track && event.track.kind === 'audio') {
                    const stream = event.streams?.[0] || new MediaStream([event.track]);
                    console.log('Stream tracks:', stream.getTracks().length);

                    let remoteAudio = remoteAudioRefs.current[targetSocketId];
                    if (!remoteAudio) {
                        remoteAudio = document.createElement('audio');
                        remoteAudio.id = `remote-audio-${targetSocketId}`;
                        remoteAudio.autoplay = true;
                        remoteAudio.playsInline = true;
                        remoteAudio.setAttribute('playsinline', ''); // iOS Safari
                        remoteAudio.setAttribute('webkit-playsinline', ''); // Older iOS
                        remoteAudio.volume = 1.0;
                        remoteAudio.muted = false;
                        // Hide audio element - it plays in background
                        remoteAudio.style.display = 'none';
                        document.body.appendChild(remoteAudio);
                        remoteAudioRefs.current[targetSocketId] = remoteAudio;
                        console.log('Created audio element for:', targetSocketId);
                    }

                    remoteAudio.srcObject = stream;

                    // Monitor audio levels to verify audio is actually being received
                    try {
                        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                        const source = audioContext.createMediaStreamSource(stream);
                        const analyser = audioContext.createAnalyser();
                        analyser.fftSize = 256;
                        source.connect(analyser);
                        // Don't connect to destination - we just want to monitor

                        const dataArray = new Uint8Array(analyser.frequencyBinCount);
                        let silenceCount = 0;

                        const checkAudioLevel = () => {
                            analyser.getByteFrequencyData(dataArray);
                            const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

                            if (average > 5) {
                                console.log('🔊 Audio level:', average.toFixed(1));
                                silenceCount = 0;
                            } else {
                                silenceCount++;
                                if (silenceCount === 50) { // After ~5 seconds of silence
                                    console.log('⚠️ No audio detected - speaker may be muted or not speaking');
                                }
                            }
                        };

                        // Check every 100ms for 10 seconds
                        const intervalId = setInterval(checkAudioLevel, 100);
                        setTimeout(() => clearInterval(intervalId), 10000);
                    } catch (e) {
                        console.log('Could not create audio analyzer:', e.message);
                    }

                    // Try to play with multiple attempts for iOS
                    const tryPlay = () => {
                        remoteAudio.play()
                            .then(() => {
                                console.log('✓ Audio playing from:', targetSocketId);
                                console.log('Audio paused:', remoteAudio.paused, 'muted:', remoteAudio.muted);
                            })
                            .catch(e => {
                                console.log('Autoplay blocked:', e.message);
                                setMessages(prev => {
                                    // Only add message once
                                    if (!prev.some(m => m.text === 'Tap the screen to enable audio')) {
                                        return [...prev, { user: 'System', text: 'Tap the screen to enable audio', isSystem: true }];
                                    }
                                    return prev;
                                });
                            });
                    };

                    tryPlay();

                    // Also try on any user interaction
                    const resumeAll = () => {
                        console.log('User interaction - trying to resume audio');
                        Object.values(remoteAudioRefs.current).forEach(a => {
                            if (a.paused && a.srcObject) {
                                a.play().then(() => console.log('Resumed audio')).catch(() => {});
                            }
                        });
                    };
                    document.addEventListener('click', resumeAll, { once: true });
                    document.addEventListener('touchstart', resumeAll, { once: true });
                    document.addEventListener('touchend', resumeAll, { once: true });
                }
            };

            peerConnection.onconnectionstatechange = () => {
                console.log('Connection state:', targetSocketId, peerConnection.connectionState);
                if (peerConnection.connectionState === 'failed') {
                    // Only cleanup on failed - disconnected can recover
                    peerConnection.close();
                    delete peerConnections.current[targetSocketId];
                    const audioEl = document.getElementById(`remote-audio-${targetSocketId}`);
                    if (audioEl) { audioEl.srcObject = null; audioEl.remove(); }
                    delete remoteAudioRefs.current[targetSocketId];
                } else if (peerConnection.connectionState === 'connected') {
                    console.log('✓ Peer connection established with:', targetSocketId);
                }
            };

            // Add local tracks if we're a speaker
            if (localStream.current) {
                console.log('Adding local tracks to peer connection');
                localStream.current.getTracks().forEach(track => {
                    console.log('Adding track:', track.kind, 'enabled:', track.enabled, 'readyState:', track.readyState);
                    peerConnection.addTrack(track, localStream.current);
                });
            } else {
                console.log('No local stream - this peer will only receive audio');
            }

            // Log negotiation events
            peerConnection.onnegotiationneeded = () => {
                console.log('Negotiation needed for:', targetSocketId);
            };

            return peerConnection;
        };
        createPeerConnectionRef.current = createPeerConnection;

        const initiateCall = async (targetSocketId) => {
            try {
                console.log('=== INITIATING CALL ===');
                console.log('To:', targetSocketId);
                console.log('Have localStream:', !!localStream.current);

                const existingPc = peerConnections.current[targetSocketId];
                if (existingPc) {
                    const connState = existingPc.connectionState;
                    const sigState = existingPc.signalingState;

                    // Skip if already connected, connecting, or negotiating
                    if (connState === 'connected' || connState === 'connecting') {
                        console.log('Already connected/connecting, skipping');
                        return;
                    }
                    if (sigState === 'have-local-offer' || sigState === 'have-remote-offer') {
                        console.log('Already negotiating, skipping');
                        return;
                    }

                    existingPc.close();
                    delete peerConnections.current[targetSocketId];
                }

                const pc = createPeerConnection(targetSocketId);
                const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: false });
                await pc.setLocalDescription(offer);
                console.log('Sending offer to:', targetSocketId);
                socket.current.emit('offer', { to: targetSocketId, offer });
            } catch (error) {
                console.error('Error initiating call:', error);
            }
        };
        initiateCallRef.current = initiateCall;

        const handleOffer = async (offer, fromSocketId) => {
            try {
                console.log('Handling offer from:', fromSocketId);
                console.log('Offer SDP has audio:', offer.sdp?.includes('m=audio'));

                let pc = peerConnections.current[fromSocketId];

                // Handle glare (both sides sent offers)
                if (pc && pc.signalingState === 'have-local-offer') {
                    // Use socket ID comparison to decide who wins (lower ID is "polite")
                    const imPolite = socket.current.id < fromSocketId;
                    console.log('Glare detected! I am', imPolite ? 'polite (will rollback)' : 'impolite (will ignore)');

                    if (!imPolite) {
                        // I'm impolite, ignore incoming offer
                        console.log('Ignoring incoming offer, waiting for my offer to be answered');
                        return;
                    }
                    // I'm polite, rollback my offer and accept theirs
                    await pc.setLocalDescription({ type: 'rollback' });
                } else if (pc) {
                    // Existing connection in different state - close and recreate
                    pc.close();
                    delete peerConnections.current[fromSocketId];
                    pc = null;
                }

                if (!pc) {
                    pc = createPeerConnection(fromSocketId);
                }

                await pc.setRemoteDescription(new RTCSessionDescription(offer));

                // Process queued ICE candidates
                const candidates = pendingIceCandidates.current[fromSocketId] || [];
                for (const candidate of candidates) {
                    await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {});
                }
                delete pendingIceCandidates.current[fromSocketId];

                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                console.log('Sending answer to:', fromSocketId);
                socket.current.emit('answer', { to: fromSocketId, answer });
            } catch (error) {
                console.error('Error handling offer:', error);
            }
        };

        const handleAnswer = async (answer, fromSocketId) => {
            try {
                console.log('Handling answer from:', fromSocketId);
                const pc = peerConnections.current[fromSocketId];
                if (pc && pc.signalingState === 'have-local-offer') {
                    await pc.setRemoteDescription(new RTCSessionDescription(answer));

                    // Process queued ICE candidates
                    const candidates = pendingIceCandidates.current[fromSocketId] || [];
                    for (const candidate of candidates) {
                        await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {});
                    }
                    delete pendingIceCandidates.current[fromSocketId];
                }
            } catch (error) {
                console.error('Error handling answer:', error);
            }
        };

        const handleIceCandidate = async (candidate, fromSocketId) => {
            const pc = peerConnections.current[fromSocketId];
            if (pc?.remoteDescription) {
                await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {});
            } else {
                if (!pendingIceCandidates.current[fromSocketId]) {
                    pendingIceCandidates.current[fromSocketId] = [];
                }
                pendingIceCandidates.current[fromSocketId].push(candidate);
            }
        };

        // Connect to socket
        console.log('Connecting to voice room server:', ENDPOINT);
        socket.current = socketIOClient(ENDPOINT, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000
        });

        socket.current.on('connect', () => {
            console.log('Socket connected! ID:', socket.current.id);
            setIsConnected(true);
            socket.current.emit('join-voice-room', {
                oderId: activeUser.SID,
                name: activeUser.name,
                odername: activeUser.name,
                picture: activeUser.picture,
                SID: activeUser.SID,
                isGuest: activeUser.isGuest || false
            });
        });

        socket.current.on('connect_error', (error) => {
            console.error('Socket connection error:', error);
            setIsConnected(false);
        });

        socket.current.on('disconnect', (reason) => {
            console.log('Socket disconnected:', reason);
            setIsConnected(false);
        });

        // Seat updates - seat 0 is host
        socket.current.on('host-seat-update', (hostData) => {
            console.log('Host seat update:', hostData);
            // Skip if we just made a local seat switch to avoid race condition
            if (recentSeatSwitch.current) {
                console.log('Skipping host-seat-update due to recent local switch');
                return;
            }
            // Update seat 0 (host seat) - but preserve current user's data if they're the host
            setSeats(prevSeats => {
                const updated = [...prevSeats];
                // If current user is in host seat (seat 0), preserve their data
                if (prevSeats[0]?.oderId === currentUser?.SID && !hostData) {
                    console.log('Preserving current user host seat data');
                    return prevSeats;
                }
                // If hostData is for a different user than current user in seat 0, and current user is host, preserve
                if (prevSeats[0]?.oderId === currentUser?.SID && hostData?.oderId !== currentUser?.SID) {
                    console.log('Preserving current user as host, ignoring stale update');
                    return prevSeats;
                }
                updated[0] = hostData;
                return updated;
            });
        });

        socket.current.on('seats-update', (seatsData) => {
            console.log('Seats update:', seatsData);
            // Skip if we just made a local seat switch to avoid race condition
            if (recentSeatSwitch.current) {
                console.log('Skipping seats-update due to recent local switch');
                return;
            }
            // Ensure we have 9 seats and preserve current user's seat
            setSeats(prevSeats => {
                const newSeats = Array(9).fill(null);
                if (Array.isArray(seatsData)) {
                    seatsData.forEach((seat, i) => {
                        if (i < 9) newSeats[i] = seat;
                    });
                }
                // Find current user's seat in previous state
                const myPrevSeatIndex = prevSeats.findIndex(s => s?.oderId === currentUser?.SID);
                if (myPrevSeatIndex !== -1) {
                    // Check if new data lost our seat (server sent stale data)
                    const myNewSeatIndex = newSeats.findIndex(s => s?.oderId === currentUser?.SID);
                    if (myNewSeatIndex === -1) {
                        // We were removed incorrectly, preserve our seat
                        console.log('Preserving current user seat at index', myPrevSeatIndex);
                        newSeats[myPrevSeatIndex] = prevSeats[myPrevSeatIndex];
                    }
                }
                return newSeats;
            });
        });

        socket.current.on('listeners-update', (listenersList) => {
            console.log('Listeners update:', listenersList?.length);
            setListeners(listenersList || []);
            setRoomInfo(prev => ({ ...prev, listeners: listenersList?.length || 0 }));
        });

        socket.current.on('user-muted', ({ oderId, isMuted: muted }) => {
            // Update muted status for any seat (including host at seat 0)
            setSeats(prevSeats => prevSeats.map(seat =>
                seat?.oderId === oderId ? { ...seat, isMuted: muted } : seat
            ));
        });

        // Listen for speaking events from other users
        socket.current.on('user-speaking', ({ oderId, speaking }) => {
            setSpeakingUsers(prev => ({ ...prev, [oderId]: speaking }));
        });

        socket.current.on('chatMessage', (message) => {
            setMessages(prevMessages => [...prevMessages, message]);
            scrollToBottom();
        });

        socket.current.on('system-message', (text) => {
            console.log('System:', text);
            setMessages(prevMessages => [...prevMessages, { user: 'System', text, isSystem: true }]);
            scrollToBottom();
        });

        // Handle bot audio (TTS from karaoke bots)
        socket.current.on('bot-audio', ({ oderId, name, audioContent, contentType, type, lyrics }) => {
            console.log('Received bot audio from:', name, 'type:', type);

            const audioData = { oderId, name, audioContent, contentType, type, lyrics };

            // Check if audio is properly unlocked (user has clicked/tapped)
            // sharedAudioRef.current only exists after user interaction via unlockAudio()
            const isAudioUnlocked = sharedAudioRef.current !== null;

            if (isAudioUnlocked) {
                // Audio is unlocked, play directly or queue if something is playing
                if (sharedAudioRef.current && !sharedAudioRef.current.paused) {
                    // Something is already playing, queue this
                    audioQueueRef.current.push(audioData);
                    console.log('Queued bot audio, queue size:', audioQueueRef.current.length);
                } else if (playBotAudioRef.current) {
                    playBotAudioRef.current(audioData);
                }
            } else {
                // Audio not unlocked yet, queue it
                audioQueueRef.current.push(audioData);
                console.log('Audio locked, queued bot audio. Queue size:', audioQueueRef.current.length);

                // Show message to user to tap
                setMessages(prev => {
                    if (!prev.some(m => m.text === '🔊 Click the red button above to hear the poems!')) {
                        return [...prev, { user: 'System', text: '🔊 Click the red button above to hear the poems!', isSystem: true }];
                    }
                    return prev;
                });
            }
        });

        // WebRTC signaling
        socket.current.on('offer', ({ from, offer }) => {
            console.log('Received offer from:', from);
            handleOffer(offer, from);
        });

        socket.current.on('answer', ({ from, answer }) => {
            console.log('Received answer from:', from);
            handleAnswer(answer, from);
        });

        socket.current.on('ice-candidate', ({ from, candidate }) => {
            handleIceCandidate(candidate, from);
        });

        // New speaker/listener events
        // When a new speaker joins, EVERYONE (listeners and other speakers) should connect to them
        socket.current.on('new-speaker', ({ oderId, socketId, name }) => {
            console.log('New speaker:', name, socketId);
            console.log('My oderId:', activeUser.SID, 'Speaker oderId:', oderId);
            console.log('I have localStream:', !!localStream.current);

            if (oderId !== activeUser.SID) {
                // Give the new speaker time to set up their broadcast
                // Use longer delay to avoid race with their setupBroadcast
                const delay = localStream.current ? 1500 : 800; // Speakers wait longer
                console.log('Will initiate call to new speaker in', delay, 'ms...');

                setTimeout(() => {
                    // Check if we already have a connection
                    const existingPc = peerConnections.current[socketId];
                    if (existingPc) {
                        const state = existingPc.connectionState;
                        if (state === 'connected' || state === 'connecting') {
                            console.log('Already connected to new speaker:', name);
                            return;
                        }
                    }
                    console.log('Now initiating call to new speaker:', socketId);
                    initiateCall(socketId);
                }, delay);
            }
        });

        // When a new listener joins, only SPEAKERS need to connect to them
        socket.current.on('new-listener', ({ oderId, socketId, name }) => {
            console.log('New listener:', name, socketId);
            console.log('I have localStream:', !!localStream.current, 'listener oderId:', oderId);
            if (localStream.current && oderId !== activeUser.SID) {
                console.log('Will initiate call to listener in 500ms...');
                setTimeout(() => {
                    console.log('Now initiating call to listener:', socketId);
                    initiateCall(socketId);
                }, 500);
            }
        });

        // List of current speakers - everyone should connect to receive audio
        socket.current.on('speakers-list', (speakersList) => {
            console.log('Speakers list received:', speakersList.length, speakersList);
            // All users (listeners and speakers) connect to speakers to receive their audio
            speakersList.forEach((speaker, idx) => {
                console.log('Checking speaker:', speaker.name, 'oderId:', speaker.oderId, 'my oderId:', activeUser.SID);
                if (speaker.oderId !== activeUser.SID && speaker.socketId) {
                    // Stagger connections and add a base delay after broadcast setup
                    const delay = 800 + (idx * 400);
                    console.log('Will initiate call to speaker', speaker.name, 'in', delay, 'ms');
                    setTimeout(() => {
                        // Check if we already have a connection before initiating
                        const existingPc = peerConnections.current[speaker.socketId];
                        if (existingPc && (existingPc.connectionState === 'connected' || existingPc.connectionState === 'connecting')) {
                            console.log('Already connected to speaker:', speaker.name);
                            return;
                        }
                        console.log('Now initiating call to speaker from list:', speaker.socketId);
                        initiateCall(speaker.socketId);
                    }, delay);
                }
            });
        });

        // Only speakers need to broadcast to listeners
        socket.current.on('listeners-for-broadcast', (listenersList) => {
            console.log('Listeners for broadcast:', listenersList?.length);
            if (localStream.current && listenersList?.length > 0) {
                listenersList.forEach((listener, idx) => {
                    if (listener.oderId !== activeUser.SID && listener.socketId) {
                        // Stagger listener connections
                        const delay = 300 + (idx * 300);
                        setTimeout(() => {
                            const existingPc = peerConnections.current[listener.socketId];
                            if (existingPc && (existingPc.connectionState === 'connected' || existingPc.connectionState === 'connecting')) {
                                console.log('Already connected to listener:', listener.name);
                                return;
                            }
                            console.log('Initiating call to listener:', listener.socketId);
                            initiateCall(listener.socketId);
                        }, delay);
                    }
                });
            }
        });

        return () => {
            if (localStream.current) {
                localStream.current.getTracks().forEach(track => track.stop());
            }
            Object.values(peerConnections.current).forEach(pc => pc.close());
            Object.keys(remoteAudioRefs.current).forEach(key => {
                const audioEl = document.getElementById(`remote-audio-${key}`);
                if (audioEl) { audioEl.srcObject = null; audioEl.remove(); }
            });
            remoteAudioRefs.current = {};
            socket.current?.disconnect();
        };
    }, [currentUser]);

    const scrollToBottom = useCallback(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, []);

    // Scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    // Waitress state for animation phases
    const [waitressPhase, setWaitressPhase] = useState('hidden'); // hidden, entering, stopped, leaving
    const [grabbedItem, setGrabbedItem] = useState(null);
    const [flyingItemPos, setFlyingItemPos] = useState(null); // {startX, startY, endX, endY}

    // Waitress walks in, stops, then leaves
    useEffect(() => {
        const showWaitress = () => {
            // Pick 4 random items for the tray
            const shuffled = [...WAITRESS_ITEMS].sort(() => Math.random() - 0.5);
            setWaitressItems(shuffled.slice(0, 4));
            setWaitressVisible(true);
            setWaitressPhase('entering');

            // After 3s, stop in middle
            setTimeout(() => {
                setWaitressPhase('stopped');
            }, 3000);

            // After 10s total (7s stopped), start leaving
            setTimeout(() => {
                setWaitressPhase('leaving');
            }, 10000);

            // After 13s, hide completely
            setTimeout(() => {
                setWaitressVisible(false);
                setWaitressPhase('hidden');
            }, 13000);
        };

        // Show waitress after 12 seconds initially
        const initialTimeout = setTimeout(showWaitress, 12000);

        // Then every 2 minutes
        const interval = setInterval(() => {
            if (!waitressVisible) {
                showWaitress();
            }
        }, 120000);

        return () => {
            clearTimeout(initialTimeout);
            clearInterval(interval);
        };
    }, [waitressVisible]);

    const takeItem = (item, event) => {
        // Get tray item position (start position)
        const trayRect = event.target.getBoundingClientRect();
        const startX = trayRect.left + trayRect.width / 2;
        const startY = trayRect.top + trayRect.height / 2;

        // Calculate end position based on user's seat or chat area
        let endX, endY;

        if (occupiedSeatIndex !== null) {
            // User is on a seat - animate to their seat
            const seatEl = document.querySelector(`.circular-seat:nth-child(${occupiedSeatIndex + 1})`);
            if (seatEl) {
                const seatRect = seatEl.getBoundingClientRect();
                endX = seatRect.left + seatRect.width / 2;
                endY = seatRect.top + seatRect.height / 2;
            } else {
                // Fallback to center
                endX = window.innerWidth / 2;
                endY = window.innerHeight / 2;
            }
        } else {
            // User is not on a seat - animate to chat area
            const chatEl = document.querySelector('.chat-section');
            if (chatEl) {
                const chatRect = chatEl.getBoundingClientRect();
                endX = chatRect.left + chatRect.width / 2;
                endY = chatRect.top + 50; // Top of chat area
            } else {
                endX = window.innerWidth / 2;
                endY = window.innerHeight - 100;
            }
        }

        setGrabbedItem(item);
        setFlyingItemPos({ startX, startY, endX, endY });

        // Animate grab then set drink
        setTimeout(() => {
            setMyDrink(item);
            setGrabbedItem(null);
            setFlyingItemPos(null);
            setTakenItems(prev => [...prev, item]);

            // Show system message
            setMessages(prev => [...prev, {
                user: 'System',
                text: `${activeUser?.name} grabbed ${item.emoji} ${item.name}`,
                isSystem: true
            }]);

            // Notify others via socket
            if (socket.current) {
                socket.current.emit('sendChatMessage', {
                    user: 'System',
                    text: `${activeUser?.name} grabbed ${item.emoji} ${item.name}`,
                    isSystem: true
                });
            }
        }, 600);

        // Remove drink after 2 minutes
        setTimeout(() => {
            setMyDrink(null);
        }, 120000);
    };

    // Update debug info periodically
    useEffect(() => {
        const interval = setInterval(() => {
            const connCount = Object.keys(peerConnections.current).length;
            const audioCount = Object.keys(remoteAudioRefs.current).length;
            setDebugInfo({ connections: connCount, audioElements: audioCount });
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    // Debug function to check audio status and try to fix issues
    const checkAudioStatus = () => {
        console.log('=== AUDIO DEBUG ===');
        console.log('My socket ID:', socket.current?.id);
        console.log('My SID:', activeUser?.SID);
        console.log('Peer connections:', Object.keys(peerConnections.current).length);
        console.log('Local stream:', !!localStream.current);
        console.log('Am I speaker:', occupiedSeatIndex !== null);

        Object.entries(peerConnections.current).forEach(([id, pc]) => {
            console.log(`Connection ${id}:`, {
                connectionState: pc.connectionState,
                iceConnectionState: pc.iceConnectionState,
                signalingState: pc.signalingState,
                senders: pc.getSenders().length,
                receivers: pc.getReceivers().length
            });
        });

        console.log('Audio elements:', Object.keys(remoteAudioRefs.current).length);
        Object.entries(remoteAudioRefs.current).forEach(([id, audio]) => {
            console.log(`Audio ${id}: paused=${audio.paused}, srcObject=${!!audio.srcObject}`);
            if (audio.paused && audio.srcObject) {
                audio.play().catch(e => console.log('Play failed:', e.message));
            }
        });

        if (localStream.current) {
            localStream.current.getTracks().forEach(track => {
                console.log('Local track:', track.kind, 'enabled:', track.enabled, 'readyState:', track.readyState);
            });
        }

        // Trigger request-listeners again to re-establish connections
        if (localStream.current) {
            console.log('Re-requesting listeners...');
            socket.current.emit('request-listeners');
        }

        alert(`Connections: ${Object.keys(peerConnections.current).length}\nAudio elements: ${Object.keys(remoteAudioRefs.current).length}\nLocal stream: ${!!localStream.current}\nSocket: ${socket.current?.id}`);
    };

    const getAudioStream = async () => {
        // Prevent multiple simultaneous requests
        if (requestingMic) {
            console.log('Already requesting microphone, skipping...');
            return null;
        }

        // If we already know permission is denied, show message instead of requesting again
        if (micPermissionDenied) {
            setMessages(prev => {
                if (!prev.some(m => m.text?.includes('Microphone access denied'))) {
                    return [...prev, {
                        user: 'System',
                        text: '🎤 Microphone access denied. Please enable microphone in browser settings and refresh the page.',
                        isSystem: true
                    }];
                }
                return prev;
            });
            return null;
        }

        setRequestingMic(true);

        try {
            console.log('Requesting microphone access...');
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                },
                video: false
            });
            localStream.current = stream;
            setMicPermissionDenied(false); // Reset if successful

            // Ensure track is enabled
            stream.getAudioTracks().forEach(track => {
                track.enabled = true;
                console.log('Audio track obtained:', track.label, 'enabled:', track.enabled);
            });

            // Setup speaking detection for local user
            try {
                const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                const source = audioContext.createMediaStreamSource(stream);
                const analyser = audioContext.createAnalyser();
                analyser.fftSize = 256;
                analyser.smoothingTimeConstant = 0.5;
                source.connect(analyser);

                const dataArray = new Uint8Array(analyser.frequencyBinCount);
                let speakingTimeout = null;

                const checkSpeaking = () => {
                    if (!localStream.current) return;

                    analyser.getByteFrequencyData(dataArray);
                    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

                    // Threshold for speaking detection
                    if (average > 15 && !isMuted) {
                        setSpeakingUsers(prev => ({ ...prev, [activeUser.SID]: true }));
                        // Notify others that I'm speaking
                        socket.current?.emit('user-speaking', { oderId: activeUser.SID, speaking: true });

                        // Clear previous timeout
                        if (speakingTimeout) clearTimeout(speakingTimeout);
                        // Set timeout to stop speaking indicator
                        speakingTimeout = setTimeout(() => {
                            setSpeakingUsers(prev => ({ ...prev, [activeUser.SID]: false }));
                            socket.current?.emit('user-speaking', { oderId: activeUser.SID, speaking: false });
                        }, 300);
                    }

                    requestAnimationFrame(checkSpeaking);
                };

                checkSpeaking();
            } catch (e) {
                console.log('Could not setup speaking detection:', e.message);
            }

            setRequestingMic(false);
            return stream;
        } catch (error) {
            console.error('Error accessing microphone:', error);
            setRequestingMic(false);

            // Check error type and provide appropriate message
            let errorMessage = '🎤 ';
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                setMicPermissionDenied(true);
                errorMessage += 'Microphone access denied. Please allow microphone permission in your browser settings.';
            } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                errorMessage += 'No microphone found. Please connect a microphone and try again.';
            } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                errorMessage += 'Microphone is being used by another app. Please close other apps using the mic.';
            } else {
                errorMessage += 'Could not access microphone. Please check your device settings.';
            }

            // Show message in chat instead of alert
            setMessages(prev => {
                if (!prev.some(m => m.text === errorMessage)) {
                    return [...prev, { user: 'System', text: errorMessage, isSystem: true }];
                }
                return prev;
            });

            return null;
        }
    };

    // Unified seat click handler - seat 0 is host
    const handleSeatClick = async (index) => {
        // Block anonymous users from taking seats
        if (isAnonymous) {
            setMessages(prev => {
                if (!prev.some(m => m.text?.includes('Please login'))) {
                    return [...prev, { user: 'System', text: '🔐 Please login to take a seat and speak', isSystem: true }];
                }
                return prev;
            });
            return;
        }

        const isHostSeat = index === 0;

        // If this seat is taken by someone else, do nothing
        if (seats[index] && seats[index].oderId !== activeUser.SID) return;

        // If I'm already in this exact seat, do nothing
        if (occupiedSeatIndex === index) return;

        const userData = {
            oderId: activeUser.SID,
            name: activeUser.name,
            picture: activeUser.picture,
            SID: activeUser.SID,
            isMuted: isMuted
        };

        // If user is in another seat, switch seats
        if (occupiedSeatIndex !== null) {
            // Set flag to prevent server updates from overriding our local state
            recentSeatSwitch.current = true;

            socket.current.emit('switch-seat', {
                fromIndex: occupiedSeatIndex,
                toIndex: index,
                fromIsHost: occupiedSeatIndex === 0,
                toIsHost: isHostSeat,
                user: userData
            });

            setSeats(prevSeats => {
                const updatedSeats = [...prevSeats];
                updatedSeats[occupiedSeatIndex] = null;
                updatedSeats[index] = userData;
                return updatedSeats;
            });
            setOccupiedSeatIndex(index);
            setIsHost(isHostSeat);

            // Clear the flag after server has had time to process
            setTimeout(() => {
                recentSeatSwitch.current = false;
            }, 1000);
            return;
        }

        // New user taking a seat - need microphone
        const stream = await getAudioStream();
        if (!stream) return;

        // Set flag to prevent server updates from overriding our local state
        recentSeatSwitch.current = true;

        setSeats(prevSeats => {
            const updatedSeats = [...prevSeats];
            updatedSeats[index] = userData;
            return updatedSeats;
        });
        setOccupiedSeatIndex(index);
        setIsHost(isHostSeat);
        socket.current.emit('take-seat', { index, isHostSeat, user: userData });

        // Clear the flag after server has had time to process
        setTimeout(() => {
            recentSeatSwitch.current = false;
        }, 1000);

        // Setup WebRTC for broadcasting
        setupBroadcast();
    };

    const setupBroadcast = async () => {
        console.log('=== SETUP BROADCAST ===');
        console.log('Local stream:', !!localStream.current);

        if (!localStream.current) {
            console.log('No local stream, skipping broadcast setup');
            return;
        }

        // Close all existing connections and re-establish them with our audio
        // This ensures clean bi-directional audio
        const existingSocketIds = Object.keys(peerConnections.current);
        console.log('Closing existing connections for re-negotiation:', existingSocketIds.length);

        existingSocketIds.forEach(socketId => {
            const pc = peerConnections.current[socketId];
            if (pc) {
                pc.close();
                delete peerConnections.current[socketId];
            }
            // Remove audio elements too - they'll be recreated
            const audioEl = document.getElementById(`remote-audio-${socketId}`);
            if (audioEl) {
                audioEl.srcObject = null;
                audioEl.remove();
            }
            delete remoteAudioRefs.current[socketId];
        });

        // Wait for new-speaker event to propagate to other users
        // Other speakers will initiate connections to us after receiving new-speaker
        console.log('Waiting for new-speaker event to propagate...');
        await new Promise(resolve => setTimeout(resolve, 500));

        // Request list of listeners AND speakers to establish fresh connections
        // This will connect us to listeners and any speakers who haven't connected yet
        socket.current.emit('request-listeners');

        console.log('Broadcast setup complete, waiting for connections...');
    };

    const handleMuteToggle = (e) => {
        e.stopPropagation();
        if (localStream.current) {
            const audioTrack = localStream.current.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = isMuted;
                setIsMuted(!isMuted);
                socket.current.emit('mute-toggle', {
                    oderId: activeUser.SID,
                    isMuted: !isMuted
                });
            }
        }
    };

    const handleLeaveSeat = (e) => {
        e.stopPropagation();
        if (occupiedSeatIndex === null) return;

        if (localStream.current) {
            localStream.current.getTracks().forEach(track => track.stop());
            localStream.current = null;
        }

        Object.values(peerConnections.current).forEach(pc => pc.close());
        peerConnections.current = {};

        const wasHost = occupiedSeatIndex === 0;

        setSeats(prevSeats => {
            const updatedSeats = [...prevSeats];
            updatedSeats[occupiedSeatIndex] = null;
            return updatedSeats;
        });
        socket.current.emit('leave-seat', { index: occupiedSeatIndex, isHostSeat: wasHost });

        setOccupiedSeatIndex(null);
        setIsHost(false);
        setIsMuted(false);
    };

    const handleSendMessage = () => {
        if (currentMessage.trim()) {
            const message = {
                user: activeUser.name,
                text: currentMessage,
                picture: activeUser.picture,
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            socket.current.emit('sendChatMessage', message);
            setMessages(prevMessages => [...prevMessages, message]);
            setCurrentMessage("");
            scrollToBottom();
        }
    };

    const getUserAvatar = (picture) => {
        if (!picture) return CDN + 'users/default-avatar.png';
        if (picture.includes('googleusercontent') || picture.startsWith('http')) return picture;
        // Bot avatars are stored in gsb/ folder
        if (picture.startsWith('gsb/')) return CDN + picture;
        return CDN + 'users/' + picture;
    };

    // Anonymous users can listen but not take seats

    return (
        <>
            <Helmet>
                <title>GS Conference Room - Live Voice Chat | Geet Suhane</title>
                <meta name="description" content="Join GS Conference Room - Live voice chat with poetry recitation by AI bots. Listen to Hindi poems, share your thoughts, and connect with music lovers." />
                <meta name="keywords" content="voice chat, live audio room, Hindi poetry, karaoke, music chat, Geet Suhane" />
                <meta property="og:title" content="GS Conference Room - Live Voice Chat" />
                <meta property="og:description" content="Join live voice chat with poetry recitation. Listen to Hindi poems and connect with music lovers!" />
                <meta property="og:type" content="website" />
            </Helmet>
            <div className="voice-chat-room drawing-room" onClick={unlockAudio} onTouchStart={unlockAudio}>
            {/* Room Header */}
            <div className="room-header">
                <div className="room-header-left">
                    <button className="hamburger-btn" onClick={() => setMenuOpen(true)}>
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>
                </div>
                <div className="room-header-center">
                    <h2>{roomInfo.name}</h2>
                </div>
                <div className="room-header-right">
                    <div className="participant-count">
                        <i className="feather-users"></i>
                        <span>{seats.filter(s => s).length + listeners.length}</span>
                    </div>
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <i className="feather-x"></i>
                    </button>
                </div>
            </div>

            {/* Flashing Enable Speaker Prompt */}
            {!audioUnlocked && (
                <div
                    className="enable-speaker-prompt"
                    onClick={unlockAudio}
                    style={{
                        position: 'fixed',
                        top: '60px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#ff3333',
                        color: 'white',
                        padding: '12px 24px',
                        borderRadius: '25px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        zIndex: 9999,
                        cursor: 'pointer',
                        boxShadow: '0 4px 15px rgba(255,0,0,0.4)',
                        animation: 'flashPulse 1s ease-in-out infinite',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <span style={{ fontSize: '18px' }}>🔊</span>
                    TAP HERE & TURN UP VOLUME
                </div>
            )}
            <style>{`
                @keyframes flashPulse {
                    0%, 100% { opacity: 1; transform: translateX(-50%) scale(1); }
                    50% { opacity: 0.7; transform: translateX(-50%) scale(1.05); }
                }
            `}</style>

            {/* Menu Overlay */}
            <div
                className={`menu-overlay ${menuOpen ? 'active' : ''}`}
                onClick={() => setMenuOpen(false)}
            />

            {/* Side Menu */}
            <nav className={`side-menu ${menuOpen ? 'active' : ''}`}>
                <div className="menu-header">
                    <h3>Menu</h3>
                    <button className="close-menu-btn" onClick={() => setMenuOpen(false)}>
                        <i className="feather-x"></i>
                    </button>
                </div>
                <div className="menu-content">
                    {/* User Info */}
                    <div className="menu-user-info">
                        <img
                            src={getUserAvatar(activeUser?.picture)}
                            alt={activeUser?.name}
                            className="menu-user-avatar"
                        />
                        <div className="menu-user-details">
                            <h4>{activeUser?.name}</h4>
                            <p>{isAnonymous ? 'Guest (Login to speak)' : (occupiedSeatIndex !== null ? (isHost ? 'Host' : 'Speaker') : 'Listener')}</p>
                        </div>
                    </div>

                    {/* Login for anonymous users */}
                    {isAnonymous && (
                        <div className="menu-section">
                            <Link to="/login" className="menu-item" onClick={() => setMenuOpen(false)}>
                                <i className="feather-log-in"></i>
                                <span>Login to Speak</span>
                            </Link>
                        </div>
                    )}

                    {/* Navigation */}
                    <div className="menu-section">
                        <div className="menu-section-title">Navigation</div>
                        <Link to="/" className="menu-item" onClick={() => setMenuOpen(false)}>
                            <i className="feather-home"></i>
                            <span>Home</span>
                        </Link>
                        <Link to="/buzz" className="menu-item" onClick={() => setMenuOpen(false)}>
                            <i className="feather-zap"></i>
                            <span>Buzz</span>
                        </Link>
                        <Link to="/search" className="menu-item" onClick={() => setMenuOpen(false)}>
                            <i className="feather-search"></i>
                            <span>Search Members</span>
                        </Link>
                    </div>

                    {/* Room Actions */}
                    <div className="menu-section">
                        <div className="menu-section-title">Room Actions</div>
                        <div className="menu-item" onClick={() => { navigator.share?.({ title: 'Join Voice Room', url: window.location.href }); setMenuOpen(false); }}>
                            <i className="feather-share-2"></i>
                            <span>Share Room</span>
                        </div>
                        {occupiedSeatIndex !== null && (
                            <div className="menu-item" onClick={(e) => { handleLeaveSeat(e); setMenuOpen(false); }}>
                                <i className="feather-log-out"></i>
                                <span>Leave Seat</span>
                            </div>
                        )}
                    </div>
                </div>
            </nav>

            {/* Room Layout - Circular arrangement with 9 seats */}
            <div className="room-layout">
                <div className="circular-seats">
                    {/* 9 Seats in circle - Seat 0 is Host at top */}
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((index) => {
                        const seat = seats[index];
                        const isHostSeat = index === 0;
                        return (
                            <div
                                key={index}
                                className={`circular-seat ${seat ? 'occupied' : 'empty'} ${isHostSeat ? 'host-seat' : ''} ${seat && speakingUsers[seat.oderId] ? 'speaking' : ''}`}
                                onClick={() => handleSeatClick(index)}
                            >
                                {isHostSeat && <span className="crown-icon">👑</span>}
                                <div className="seat-avatar-wrap">
                                    {seat ? (
                                        <>
                                            <img src={getUserAvatar(seat.picture)} alt={seat.name} />
                                            {seat.isMuted && <div className="muted-icon"><i className="feather-mic-off"></i></div>}
                                            {occupiedSeatIndex === index && myDrink && (
                                                <span className="drink-badge">{myDrink.emoji}</span>
                                            )}
                                        </>
                                    ) : (
                                        <span className="empty-plus"><i className="feather-plus"></i></span>
                                    )}
                                </div>
                                {seat && <span className="seat-name">{seat.name}</span>}
                                {isHostSeat && seat && <span className="host-tag">HOST</span>}
                                {occupiedSeatIndex === index && (
                                    <div className="my-controls">
                                        <button onClick={handleMuteToggle} className={`ctrl-btn ${isMuted ? 'muted' : ''}`}>
                                            <i className={`feather-${isMuted ? 'mic-off' : 'mic'}`}></i>
                                        </button>
                                        <button onClick={handleLeaveSeat} className="ctrl-btn leave">
                                            <i className="feather-log-out"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Waitress animation - enters, stops in center, leaves */}
                    {waitressVisible && (
                        <div className={`waitress-container ${waitressPhase}`}>
                            <div className="waitress-figure">
                                <div className="waitress-emoji-wrap">
                                    <div className="speech-bubble">Refreshments!</div>
                                    <span className="waitress-emoji">🧑‍🍳</span>
                                </div>
                                <div className="tray">
                                    {waitressItems.map((item) => (
                                        <button
                                            key={item.id}
                                            className={`tray-item ${grabbedItem?.id === item.id ? 'grabbed' : ''}`}
                                            onClick={(e) => !grabbedItem && takeItem(item, e)}
                                            title={item.name}
                                            disabled={!!grabbedItem}
                                        >
                                            {item.emoji}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                {/* Flying item animation */}
                {grabbedItem && flyingItemPos && (
                    <div
                        className="grab-animation"
                        style={{
                            left: flyingItemPos.startX,
                            top: flyingItemPos.startY,
                            '--end-x': `${flyingItemPos.endX - flyingItemPos.startX}px`,
                            '--end-y': `${flyingItemPos.endY - flyingItemPos.startY}px`
                        }}
                    >
                        <span className="flying-item">{grabbedItem.emoji}</span>
                    </div>
                )}
            </div>

            {/* Singing mode. Renders nothing at all unless a room lead has switched
                it on, so the room is unchanged for everyone else. It reuses this
                page's socket, mic stream and AudioContext rather than opening its
                own — a second getUserMedia can hand back a differently-processed mic
                on some phones, which would mean scoring a signal the room never
                actually heard. */}
            <SingingStage
                socket={socket.current}
                activeUser={activeUser}
                localStream={localStream.current}
                audioContext={audioContextRef.current}
                isAdmin={Number(currentUser?.lead) > 0 && Number(currentUser?.lead) !== 9}
            />

            {/* Chat Section - Fixed height at bottom */}
            <div className="chat-section fixed-chat">
                <div className="chat-header">
                    <i className="feather-message-circle"></i> Live Chat
                    {myDrink && <span className="my-drink-indicator">{myDrink.emoji}</span>}
                </div>
                <div className="chat-messages">
                    {messages.map((msg, index) => (
                        <div key={index} className={`chat-message ${msg.isSystem ? 'system' : ''}`}>
                            {!msg.isSystem && (
                                <img src={getUserAvatar(msg.picture)} alt="" className="chat-avatar" />
                            )}
                            <div className="chat-content">
                                {!msg.isSystem && <span className="chat-username">{msg.user}</span>}
                                <span className="chat-text">{msg.text}</span>
                            </div>
                            {msg.timestamp && <span className="chat-time">{msg.timestamp}</span>}
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>
                <div className="chat-input-area">
                    <input
                        type="text"
                        value={currentMessage}
                        onChange={(e) => setCurrentMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        placeholder="Say something..."
                        className="chat-input"
                    />
                    <button onClick={handleSendMessage} className="send-btn">
                        <i className="feather-send"></i>
                    </button>
                </div>
            </div>
        </div>
        </>
    );
}
